import { PageOptions } from '@graphcommerce/framer-next-pages'
import { HygraphPagesQuery, hygraphPageContent } from '@graphcommerce/graphcms-ui'
import { mergeDeep } from '@graphcommerce/graphql'
import { useCustomerSession } from '@graphcommerce/magento-customer'
import {
  AddProductsToCartError,
  AddProductsToCartForm,
  AddProductsToCartFormProps,
  AddProductsToCartQuantity,
  ProductCustomizable,
  ProductPageAddToCartQuantityRow,
  ProductPageGallery,
  ProductPageJsonLd,
  ProductPageMeta,
  ProductPageName,
  ProductPagePrice,
  ProductPagePriceTiers,
  getProductStaticPaths,
  jsonLdProduct,
  jsonLdProductOffer,
  productPageCategory,
} from '@graphcommerce/magento-product'
import { BundleProductOptions } from '@graphcommerce/magento-product-bundle'
import { defaultConfigurableOptionsSelection } from '@graphcommerce/magento-product-configurable'
import { DownloadableProductOptions } from '@graphcommerce/magento-product-downloadable'
import { RecentlyViewedProducts } from '@graphcommerce/magento-recently-viewed-products'
import { jsonLdProductReview } from '@graphcommerce/magento-review'
import { Money, StoreConfigDocument, redirectOrNotFound } from '@graphcommerce/magento-store'
import {
  GetStaticProps,
  LayoutTitle,
  breakpointVal,
  isTypename,
  responsiveVal,
} from '@graphcommerce/next-ui'
import { Trans } from '@lingui/react'
import { Box, Container, Divider, Link, Typography } from '@mui/material'
import { GetStaticPaths } from 'next'
import { useRouter } from 'next/router'
import {
  LayoutHeaderVaessen,
  LayoutNavigation,
  LayoutNavigationProps,
  PaymentMethods,
  ProductSignInButton,
  RowProduct,
  RowRenderer,
  Usps,
  productListRenderer,
} from '../../components'
import { AMLabelRenderer } from '../../components/AMLabel/AMLabelRenderer'
import { brandPagesUrls } from '../../components/BrandPagesUrls/BrandPageUrls'
import { ConfigurableProductOptions } from '../../components/ConfigurableProduct/ConfigurableProductOptions'
import { ProductHreflangs } from '../../components/Hreflangs/ProductHreflangs'
import { useKlaviyoViewProductEvent } from '../../components/Klaviyo/useKlaviyoViewProductEvent'
import { LayoutDocument } from '../../components/Layout/Layout.gql'
import { AddToCartOrOutOfStockSubscribe } from '../../components/Product/AddToCartOrOutOfStockSubscribe'
import { ProductPageBreadcrumbs } from '../../components/Product/ProductPageBreadcrumbs'
import { ProductSku } from '../../components/Product/ProductSku'
import { useCustomerProductPriceSingle } from '../../components/Product/UseCustomerProductPriceSingle'
import { UspsDocument, UspsQuery } from '../../components/Usps/Usps.gql'
import { GalleryContextProvider } from '../../contexts/GalleryContext'
import { ProductPageContextProvider } from '../../contexts/ProductPageContext'
import { ProductPage2Document, ProductPage2Query } from '../../graphql/ProductPage2.gql'
import { graphqlSharedClient, graphqlSsrClient } from '../../lib/graphql/graphqlSsrClient'
import { useStoreData } from '../../lib/hooks/useStoreData'
import { ProductBulletpoints } from '../../components/Product/ProductBulletpoints'

export type ProductPageProps = HygraphPagesQuery &
  UspsQuery &
  ProductPage2Query &
  Pick<AddProductsToCartFormProps, 'defaultValues'>

type RouteProps = { url: string }
type GetPageStaticPaths = GetStaticPaths<RouteProps>
type GetPageStaticProps = GetStaticProps<LayoutNavigationProps, ProductPageProps, RouteProps>
export type ProductSingle = ProductPage2Query['products']
function ProductPage(props: ProductPageProps) {
  const { products, relatedUpsells, sidebarUsps, pages, defaultValues } = props
  const mergedProduct = mergeDeep(
    products?.items?.[0],
    relatedUpsells?.items?.find((item) => item?.uid === products?.items?.[0]?.uid),
  )

  const product = useCustomerProductPriceSingle(mergedProduct)
  const { loggedIn } = useCustomerSession()
  const { mode } = useStoreData()
  const storeB2B = mode === 'b2b'
  const loggedOutB2B = !loggedIn && storeB2B
  const { locale } = useRouter()
  useKlaviyoViewProductEvent(product, locale)

  if (!product?.sku || !product.url_key) return null

  const brandPageUrl = brandPagesUrls[`${locale}`]
  const customAttributes = product?.custom_attributes
  const brandAttribute = customAttributes?.find(
    (attribute) => attribute?.attribute_metadata?.code === 'merk',
  )
  const brandLabel =
    brandAttribute?.selected_attribute_options?.attribute_option?.map((item) => item?.label)[0] ??
    ''
  const brandSlug = brandLabel?.toLowerCase().replace(/[\s']/g, '-') ?? ''
  const youtubeLink = product.youtube_link ?? ''

  return (
    <ProductPageContextProvider>
      {product?.hreflang && <ProductHreflangs hreflangs={product.hreflang} />}
      <AddProductsToCartForm key={product.uid} defaultValues={defaultValues}>
        <LayoutHeaderVaessen
          floatingMd
          sx={(theme) => ({
            [theme.breakpoints.up('md')]: {
              '& .LayoutHeaderContent-left': {
                display: 'none',
              },
            },
          })}
        >
          <LayoutTitle component='span' size='small'>
            <ProductPageName product={product} />
          </LayoutTitle>
        </LayoutHeaderVaessen>
        <ProductPageJsonLd
          product={product}
          render={(p) => ({
            '@context': 'https://schema.org',
            ...jsonLdProduct(p),
            ...jsonLdProductOffer(p),
            ...jsonLdProductReview(p),
          })}
        />
        <ProductPageMeta product={product} />

        <Container maxWidth={false} sx={(theme) => ({ marginBottom: theme.spacings.xxs })}>
          <ProductPageBreadcrumbs
            product={product}
            breadcrumbsAmountMobile={2}
            breadcrumbsAmountDesktop={2}
            itemSx={(theme) => ({
              fontWeight: 400,
              ...breakpointVal('fontSize', 12, 13, theme.breakpoints.values),
            })}
            sx={(theme) => ({
              '& .MuiBreadcrumbs-li': {},
              '& .MuiBreadcrumbs-separator': {
                ...breakpointVal('fontSize', 12, 13, theme.breakpoints.values),
              },
            })}
          />
        </Container>

        <Box sx={{ position: 'relative' }}>
          {product?.amLabel?.items?.[0] && (
            <Box
              sx={{
                position: 'absolute',
                zIndex: 99,
                top: responsiveVal(15, 102),
                left: responsiveVal(10, 170),
              }}
            >
              <AMLabelRenderer label={product?.amLabel?.items?.[0]} />
            </Box>
          )}

          <GalleryContextProvider youtubeLink={youtubeLink}>
            <ProductPageGallery
              product={product}
              sx={(theme) => ({
                mb: theme.spacings.sm,
                '& .SidebarGallery-sidebar': {
                  display: 'grid',
                  rowGap: theme.spacings.sm,
                },
                [theme.breakpoints.down('sm')]: {
                  mb: theme.spacings.lg,
                  '& .ScrollerDots-root': {
                    transform: 'scale(0.8)',
                    maxWidth: `100vw`,
                    overflow: `hidden`,
                  },
                },
                [theme.breakpoints.down('xs')]: {
                  '& .ScrollerDots-root': {
                    transform: 'scale(0.7)',
                    maxWidth: theme.breakpoints.values.xs,
                    overflow: `hidden`,
                  },
                },
                '& picture img': {
                  maxHeight: '85vh',
                  [theme.breakpoints.up('sm')]: {
                    p: '2%',
                  },
                  [theme.breakpoints.up('md')]: {
                    p: '4%',
                  },
                  [theme.breakpoints.up('lg')]: {
                    p: '6%',
                  },
                },
              })}
            >
              <div>
                {isTypename(product, ['ConfigurableProduct', 'BundleProduct']) && (
                  <Typography component='div' variant='body2' color='text.disabled'>
                    <Trans
                      id='As low as <0/>'
                      components={{
                        0: <Money {...product.price_range.minimum_price.final_price} />,
                      }}
                    />
                  </Typography>
                )}
                <Typography variant='h3' component='h1' gutterBottom>
                  <ProductPageName product={product} />
                </Typography>
                {brandAttribute && (
                  <Link
                    href={`/${brandPageUrl}/${brandSlug}`}
                    variant='body1'
                    color='primary'
                    underline='hover'
                    sx={(theme) => ({
                      marginRight: theme.spacings.xs,
                      display: 'inline-block',
                    })}
                  >
                    {brandLabel}
                  </Link>
                )}
                <ProductSku sku={product.sku} />
                <Typography
                  variant='body1'
                  component='div'
                  dangerouslySetInnerHTML={{ __html: product?.short_description?.html ?? '' }}
                />
              </div>
              <ProductBulletpoints product={product} />
              {isTypename(product, ['ConfigurableProduct']) && (
                <ConfigurableProductOptions
                  product={product}
                  sx={(theme) => ({
                    '& .ActionCard-title.variantOutlined': {
                      fontFamily: theme.typography.h1.fontFamily,
                    },
                  })}
                />
              )}
              {isTypename(product, ['BundleProduct']) && <BundleProductOptions product={product} />}
              {isTypename(product, ['DownloadableProduct']) && (
                <DownloadableProductOptions product={product} />
              )}
              {!isTypename(product, ['GroupedProduct']) && (
                <ProductCustomizable product={product} />
              )}
              <Divider />

              <ProductPageAddToCartQuantityRow product={product}>
                {loggedOutB2B ? (
                  <ProductSignInButton />
                ) : (
                  <>
                    <AddProductsToCartQuantity sx={{ flexShrink: '0' }} />

                    <AddProductsToCartError>
                      <Typography
                        component='div'
                        variant='h3'
                        lineHeight='1'
                        color='primary.main'
                        sx={{ '& .discountPrice': { fontSize: '0.8em' } }}
                      >
                        <ProductPagePrice product={product} />
                      </Typography>
                    </AddProductsToCartError>
                  </>
                )}
              </ProductPageAddToCartQuantityRow>

              <ProductPagePriceTiers product={product} />

              <AddToCartOrOutOfStockSubscribe product={product} loggedOutB2B={loggedOutB2B} />

              <Usps usps={sidebarUsps} size='small' />

              <PaymentMethods />
            </ProductPageGallery>
          </GalleryContextProvider>
        </Box>
      </AddProductsToCartForm>

      {pages?.[0] && (
        <RowRenderer
          content={pages?.[0].content}
          loadingEager={0}
          renderer={{
            RowProduct: (rowProps) => (
              <RowProduct
                {...rowProps}
                {...product}
                items={products?.items}
                aggregations={products?.aggregations}
              />
            ),
          }}
        />
      )}

      <RecentlyViewedProducts
        title={<Trans id='Recently viewed products' />}
        exclude={[product.sku]}
        productListRenderer={productListRenderer}
        sx={(theme) => ({ mb: theme.spacings.xxl })}
      />
    </ProductPageContextProvider>
  )
}

ProductPage.pageOptions = {
  Layout: LayoutNavigation,
} as PageOptions

export default ProductPage

export const getStaticPaths: GetPageStaticPaths = async ({ locales = [] }) => {
  // Disable SSG for Product pages right now as it will make a deployment last ± 6 hours
  return { paths: [], fallback: 'blocking' }

  if (process.env.NODE_ENV === 'development') return { paths: [], fallback: 'blocking' }

  const path = (locale: string) => getProductStaticPaths(graphqlSsrClient(locale), locale)
  const paths = (await Promise.all(locales.map(path))).flat(1)

  return { paths, fallback: 'blocking' }
}

export const getStaticProps: GetPageStaticProps = async ({ params, locale }) => {
  const client = graphqlSharedClient(locale)
  const staticClient = graphqlSsrClient(locale)

  const urlKey = params?.url ?? '??'

  const conf = client.query({ query: StoreConfigDocument })
  const productPage = staticClient.query({ query: ProductPage2Document, variables: { urlKey } })
  const layout = staticClient.query({ query: LayoutDocument, fetchPolicy: 'cache-first' })

  const product = productPage.then((pp) =>
    pp.data.products?.items?.find((p) => p?.url_key === urlKey),
  )

  const pages = hygraphPageContent(staticClient, 'product/global', product, true)
  if (!(await product)) return redirectOrNotFound(staticClient, conf, params, locale)

  const category = productPageCategory(await product)
  const up =
    category?.url_path && category?.name
      ? { href: `/${category.url_path}`, title: category.name }
      : { href: `/`, title: 'Home' }
  const usps = staticClient.query({ query: UspsDocument, fetchPolicy: 'cache-first' })

  return {
    props: {
      ...defaultConfigurableOptionsSelection(urlKey, client, (await productPage).data),
      ...(await layout).data,
      ...(await pages).data,
      ...(await usps).data,
      apolloState: await conf.then(() => client.cache.extract()),
      up,
    },
    revalidate: 60 * 20,
  }
}
